import React from 'react';
import Footer from '../Footer';
import BackLink from '../BackLink';
import '../Blog.css';

function RouteDiagram() {
  return (
    <svg viewBox="0 0 680 400" role="img" aria-labelledby="byok-d-title byok-d-desc">
      <title id="byok-d-title">Where a BYOK request goes</title>
      <desc id="byok-d-desc">
        You talk to Panda on your phone, where both keys stay. The model key goes to Gemini, ChatGPT, Claude, or OpenRouter for replies and tasks. The Jev key handles quick taps and app opens, with an OpenAI decision API coming soon. Listening and speaking stay with Panda.
      </desc>
      <defs>
        <marker id="byok-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" className="byok-ah" />
        </marker>
      </defs>

      <rect x="230" y="12" width="220" height="52" rx="8" className="byok-box" />
      <text x="340" y="44" textAnchor="middle" className="byok-ink" fontSize="16" fontWeight="600">You</text>

      <line x1="340" y1="64" x2="340" y2="96" className="byok-ln" markerEnd="url(#byok-arrow)" />

      <rect x="170" y="96" width="340" height="72" rx="8" className="byok-box" />
      <text x="340" y="126" textAnchor="middle" className="byok-ink" fontSize="16" fontWeight="600">Panda, on your phone</text>
      <text x="340" y="148" textAnchor="middle" className="byok-mut" fontSize="13">both keys stay here</text>

      <path d="M200,168 V210 H110 V238" className="byok-ln" markerEnd="url(#byok-arrow)" />
      <path d="M340,168 V238" className="byok-ln" markerEnd="url(#byok-arrow)" />
      <path d="M480,168 V210 H570 V238" className="byok-ln" markerEnd="url(#byok-arrow)" />

      <rect x="16" y="242" width="196" height="140" rx="8" className="byok-box-key" />
      <text x="114" y="272" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Model key</text>
      <text x="114" y="296" textAnchor="middle" className="byok-mut" fontSize="13">replies and tasks</text>
      <text x="114" y="322" textAnchor="middle" className="byok-mut" fontSize="12">Gemini · ChatGPT</text>
      <text x="114" y="342" textAnchor="middle" className="byok-mut" fontSize="12">Claude · OpenRouter</text>

      <rect x="242" y="242" width="196" height="140" rx="8" className="byok-box-key" />
      <text x="340" y="272" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Jev key</text>
      <text x="340" y="296" textAnchor="middle" className="byok-mut" fontSize="13">quick taps, app opens</text>
      <text x="340" y="322" textAnchor="middle" className="byok-mut" fontSize="12">required for BYOK</text>
      <text x="340" y="348" textAnchor="middle" className="byok-soon" fontSize="12">OpenAI decision API, soon</text>

      <rect x="468" y="242" width="196" height="140" rx="8" className="byok-box" />
      <text x="566" y="278" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Voice</text>
      <text x="566" y="306" textAnchor="middle" className="byok-mut" fontSize="13">listening and speaking</text>
      <text x="566" y="330" textAnchor="middle" className="byok-mut" fontSize="13">stay with Panda</text>
    </svg>
  );
}

export default function ByokPlan() {
  return (
    <div className="story-page">
      <article className="story-inner">
        <BackLink to="/blog" className="story-back">Blog</BackLink>
        <h1>How the BYOK plan works</h1>
        <p className="news-meta">
          <span>Product</span>
          <span>October 2026</span>
          <span>4 min read</span>
        </p>
        <div className="story-body">
          <p>
            BYOK means bring your own key. On this plan you paste your own AI key, and that company bills you for Panda’s thinking. You are not spending Panda credits on those replies.
          </p>

          <h2>What you need</h2>
          <p>
            Two keys. One is for the model that writes and talks: Gemini, ChatGPT, Claude, or OpenRouter. Add it in Settings, pick the one you want Panda to use, and choose a model.
          </p>
          <p>
            The other is a Jev key. Jev is what decides the quick taps and app opens. BYOK mode needs that key. Without it, Panda cannot run in BYOK mode.
          </p>
          <p>
            An OpenAI decision API is coming soon, so that quick-decision step will not only be Jev.
          </p>

          <h2>Where your keys live</h2>
          <p>
            Both keys stay on your phone, inside Panda. They are not saved on your Panda account. When you ask Panda to do something, the phone sends the key with that request so we can pass it to the right company. We do not keep a copy afterward. Settings shows only the end of each key.
          </p>

          <h2>Where each request goes</h2>
          <p>
            You still sign in to Panda. After that, the work splits.
          </p>
          <figure className="byok-fig">
            <div className="byok-fig-box">
              <RouteDiagram />
            </div>
            <figcaption>
              Your model key handles replies and tasks. Your Jev key handles quick taps. Listening and speaking stay with Panda.
            </figcaption>
          </figure>
          <ul>
            <li>Replies, tasks, and anything the model has to think through go to the provider whose key you turned on.</li>
            <li>Quick decisions, the taps and app opens, go through your Jev key.</li>
            <li>Listening and speaking stay with Panda, so the phone can still hear you and talk back.</li>
          </ul>
          <p>
            If a model key runs out of quota, add another one in Settings. Panda uses the one with room left.
          </p>
        </div>
      </article>
      <Footer />
    </div>
  );
}
